
import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Check,
  AlertCircle,
} from "lucide-react";
import NewTreatmentModal from "../components/aesthetics/treatments/NewTreatmentModal";
import RescheduleTreatmentModal from "../components/aesthetics/treatments/RescheduleTreatmentModal";
import CompleteTreatmentModal from "../components/aesthetics/treatments/CompleteTreatmentModal";

// Types
type ConsentStatus = "Upcoming" | "In Progress" | "Completed" | "Not Required";
type TreatmentStatus = "Upcoming" | "In Progress" | "Completed";
type FilterTab = "all" | "upcoming" | "in_progress" | "completed";

interface Treatment {
  id: string;
  client: {
    name: string;
    avatar?: string;
    initials: string;
    avatarBg: string;
  };
  treatment: string;
  practitioner: string;
  consent: ConsentStatus;
  consentSigned?: boolean;
  status: TreatmentStatus;
  statusPending?: boolean;
  date: string;
  time: string;
}

// Static treatment data matching the design
const staticTreatments: Treatment[] = [
  {
    id: "1",
    client: {
      name: "Sarah Miller",
      initials: "SM",
      avatarBg: "bg-purple-100 text-purple-600",
    },
    treatment: "Hydrafacial",
    practitioner: "Emma Taylor",
    consent: "Upcoming",
    status: "Upcoming",
    date: "May 25, 2024",
    time: "10:00 AM",
  },
  {
    id: "2",
    client: {
      name: "James Lee",
      initials: "JL",
      avatarBg: "bg-amber-100 text-amber-600",
    },
    treatment: "Laser Hair Removal",
    practitioner: "Dr Saleema",
    consent: "Upcoming",
    status: "Upcoming",
    statusPending: true,
    date: "May 25, 2024",
    time: "11:00 AM",
  },
  {
    id: "3",
    client: {
      name: "Laura Brown",
      initials: "LB",
      avatarBg: "bg-pink-100 text-pink-600",
    },
    treatment: "Chemical Peel",
    practitioner: "Dr Saleema",
    consent: "Upcoming",
    consentSigned: true,
    status: "Upcoming",
    date: "May 26, 2024",
    time: "9:30 AM",
  },
  {
    id: "4",
    client: {
      name: "Emma Clark",
      initials: "EC",
      avatarBg: "bg-blue-100 text-blue-600",
    },
    treatment: "PRP Facial Rejuvenation",
    practitioner: "Emma Taylor",
    consent: "In Progress",
    consentSigned: true,
    status: "In Progress",
    date: "May 24, 2024",
    time: "2:00 PM",
  },
  {
    id: "5",
    client: {
      name: "Michael Davis",
      initials: "MD",
      avatarBg: "bg-emerald-100 text-emerald-600",
    },
    treatment: "Laser Skin Resurfacing",
    practitioner: "Dr Saleema",
    consent: "In Progress",
    status: "In Progress",
    statusPending: false,
    consentSigned: true,
    date: "May 24, 2024",
    time: "11:00 AM",
  },
  {
    id: "6",
    client: {
      name: "Rachel Adams",
      initials: "RA",
      avatarBg: "bg-rose-100 text-rose-600",
    },
    treatment: "Dermal Fillers",
    practitioner: "Dr Saleema",
    consent: "In Progress",
    consentSigned: true,
    status: "In Progress",
    date: "May 24, 2024",
    time: "3:45 PM",
  },
  {
    id: "7",
    client: {
      name: "Sophia Green",
      initials: "SG",
      avatarBg: "bg-teal-100 text-teal-600",
    },
    treatment: "Microneedling",
    practitioner: "Emma Taylor",
    consent: "Completed",
    consentSigned: true,
    status: "Completed",
    date: "May 22, 2024",
    time: "10:00 AM",
  },
  {
    id: "8",
    client: {
      name: "David Wilson",
      initials: "DW",
      avatarBg: "bg-indigo-100 text-indigo-600",
    },
    treatment: "Lip Filler",
    practitioner: "Emma Taylor",
    consent: "Completed",
    consentSigned: true,
    status: "Completed",
    date: "May 20, 2024",
    time: "1:30 PM",
  },
  {
    id: "9",
    client: {
      name: "Olivia Martinez",
      initials: "OM",
      avatarBg: "bg-cyan-100 text-cyan-600",
    },
    treatment: "Botox - Upper Face",
    practitioner: "Dr Saleema",
    consent: "Completed",
    consentSigned: true,
    status: "Completed",
    date: "May 18, 2024",
    time: "3:00 PM",
  },
  {
    id: "10",
    client: {
      name: "William Thompson",
      initials: "WT",
      avatarBg: "bg-violet-100 text-violet-600",
    },
    treatment: "LED Light Therapy",
    practitioner: "Emma Taylor",
    consent: "Completed",
    consentSigned: true,
    status: "Completed",
    date: "May 15, 2024",
    time: "11:30 AM",
  },
  {
    id: "11",
    client: {
      name: "Isabella Johnson",
      initials: "IJ",
      avatarBg: "bg-orange-100 text-orange-600",
    },
    treatment: "Skin Tightening",
    practitioner: "Dr Saleema",
    consent: "Completed",
    consentSigned: true,
    status: "Completed",
    date: "May 12, 2024",
    time: "2:30 PM",
  },
  {
    id: "12",
    client: {
      name: "Ethan Brown",
      initials: "EB",
      avatarBg: "bg-lime-100 text-lime-600",
    },
    treatment: "Acne Treatment",
    practitioner: "Emma Taylor",
    consent: "Completed",
    consentSigned: true,
    status: "Completed",
    date: "May 10, 2024",
    time: "10:00 AM",
  },
];

// Consent Badge Component
const ConsentBadge = ({
  status,
}: {
  status: ConsentStatus;
  signed?: boolean;
}) => {
  if (status === "Upcoming") {
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded text-[11px] font-medium bg-[#fff8e6] text-[#b86e00] border border-[#ffd89e]">
        Upcoming
      </span>
    );
  }

  if (status === "In Progress") {
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded text-[11px] font-medium bg-[#f0ebf5] text-[#704aaf] border border-[#e8e0f0]">
        In Progress
      </span>
    );
  }

  if (status === "Completed") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium bg-[#e6f7ed] text-[#0d7a3e] border border-[#b8e6cd]">
        <Check className="w-3 h-3" />
        Completed
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded text-[11px] font-medium bg-gray-100 text-[#6b6b6b]">
      Not Required
    </span>
  );
};

// Status Badge Component
const StatusBadge = ({
  status,
  pending,
  signed,
}: {
  status: TreatmentStatus;
  pending?: boolean;
  signed?: boolean;
}) => {
  if (status === "Upcoming") {
    if (pending) {
      return (
        <span className="inline-flex items-center gap-1 text-[13px] text-[#e6a700]">
          <AlertCircle className="w-3.5 h-3.5" />
          Pending
        </span>
      );
    }
    if (signed) {
      return <span className="text-[13px] text-[#3d3d3d]">Signed</span>;
    }
    return <span className="text-[13px] text-[#3d3d3d]">Upcoming</span>;
  }

  if (status === "In Progress") {
    if (signed) {
      return <span className="text-[13px] text-[#3d3d3d]">Signed</span>;
    }
    return <span className="text-[13px] text-[#6b6b6b]">Not Required</span>;
  }

  if (status === "Completed") {
    if (signed) {
      return (
        <span className="inline-flex items-center gap-1 text-[13px] text-[#0d7a3e]">
          <Check className="w-3.5 h-3.5" />
          Signed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-[13px] text-[#6b6b6b]">
        <Check className="w-3.5 h-3.5" />
        Not Required
      </span>
    );
  }

  return null;
};

// Action Buttons Component
const ActionButtons = ({
  status,
  onView,
  onReschedule,
  onResume,
  onComplete,
}: {
  status: TreatmentStatus;
  onView?: (e: React.MouseEvent) => void;
  onReschedule?: (e: React.MouseEvent) => void;
  onResume?: (e: React.MouseEvent) => void;
  onComplete?: (e: React.MouseEvent) => void;
}) => {
  if (status === "Upcoming") {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={onView}
          className="px-3 py-1.5 text-[12px] font-medium text-[#6b6b6b] bg-white border border-[#e8e0f0] rounded hover:bg-[#f8f5fc] transition-colors"
        >
          View
        </button>
        <button
          onClick={onReschedule}
          className="px-3 py-1.5 text-[12px] font-medium text-[#6b6b6b] bg-white border border-[#e8e0f0] rounded hover:bg-[#f8f5fc] transition-colors"
        >
          Reschedule
        </button>
      </div>
    );
  }

  if (status === "In Progress") {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={onResume}
          className="px-3 py-1.5 text-[12px] font-medium text-white bg-[#704aaf] rounded hover:bg-[#764BB7] transition-colors"
        >
          Resume
        </button>
        <button
          onClick={onComplete}
          className="px-3 py-1.5 text-[12px] font-medium text-[#6b6b6b] bg-white border border-[#e8e0f0] rounded hover:bg-[#f8f5fc] transition-colors"
        >
          Complete
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={onView}
      className="px-3 py-1.5 text-[12px] font-medium text-[#6b6b6b] bg-white border border-[#e8e0f0] rounded hover:bg-[#f8f5fc] transition-colors"
    >
      View
    </button>
  );
};

// Main Component
export default function AestheticTreatments() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get initial tab from URL query parameter
  const getInitialTab = (): FilterTab => {
    const tab = searchParams.get("tab");
    if (tab === "upcoming" || tab === "in_progress" || tab === "completed" || tab === "all") {
      return tab;
    }
    return "all";
  };
  
  const [activeFilter, setActiveFilter] = useState<FilterTab>(getInitialTab());
  const [showNewTreatmentMenu, setShowNewTreatmentMenu] = useState(false);
  const [showNewTreatmentModal, setShowNewTreatmentModal] = useState(false);
  const [treatmentModalMode, setTreatmentModalMode] = useState<"booking" | "walkin">("booking");
  
  // Reschedule modal state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedTreatmentForReschedule, setSelectedTreatmentForReschedule] = useState<Treatment | null>(null);
  
  // Complete modal state
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedTreatmentForComplete, setSelectedTreatmentForComplete] = useState<Treatment | null>(null);

  const openNewTreatmentModal = (mode: "booking" | "walkin") => {
    setTreatmentModalMode(mode);
    setShowNewTreatmentModal(true);
    setShowNewTreatmentMenu(false);
  };

  const handleNewTreatmentSave = (data: any) => {
    console.log("New treatment created:", data);
    // In a real app, this would make an API call to create the treatment
    // and then refresh the treatments list
  };

  const openRescheduleModal = (treatment: Treatment) => {
    setSelectedTreatmentForReschedule(treatment);
    setShowRescheduleModal(true);
  };

  const handleRescheduleSave = (data: { date: Date; time: string; reason: string }) => {
    console.log("Treatment rescheduled:", {
      treatment: selectedTreatmentForReschedule,
      newDate: data.date,
      newTime: data.time,
      reason: data.reason,
    });
    // In a real app, this would make an API call to reschedule the treatment
  };

  const openCompleteModal = (treatment: Treatment) => {
    setSelectedTreatmentForComplete(treatment);
    setShowCompleteModal(true);
  };

  const handleCompleteTreatment = (data: { notes: string; aftercareShared: boolean }) => {
    console.log("Treatment completed:", {
      treatment: selectedTreatmentForComplete,
      notes: data.notes,
      aftercareShared: data.aftercareShared,
    });
    // In a real app, this would make an API call to complete the treatment
  };

  // Calculate counts
  const counts = useMemo(() => {
    return {
      upcoming: staticTreatments.filter((t) => t.status === "Upcoming").length,
      inProgress: staticTreatments.filter((t) => t.status === "In Progress")
        .length,
      completed: staticTreatments.filter((t) => t.status === "Completed")
        .length,
    };
  }, []);

  // Filter treatments based on active filter
  const filteredTreatments = useMemo(() => {
    switch (activeFilter) {
      case "upcoming":
        return staticTreatments.filter((t) => t.status === "Upcoming");
      case "in_progress":
        return staticTreatments.filter((t) => t.status === "In Progress");
      case "completed":
        return staticTreatments.filter((t) => t.status === "Completed");
      default:
        return staticTreatments;
    }
  }, [activeFilter]);

  const tabs = [
    { id: "all" as FilterTab, label: "All", count: undefined },
    { id: "upcoming" as FilterTab, label: "Upcoming", count: counts.upcoming },
    {
      id: "in_progress" as FilterTab,
      label: "In Progress",
      count: counts.inProgress,
    },
    {
      id: "completed" as FilterTab,
      label: "Completed",
      count: counts.completed,
    },
  ];

  return (
    <div className="w-full min-h-screen bg-[#f8f5fc] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#2d2d2d]">Treatments</h1>
        <div className="relative">
          <button
            onClick={() => setShowNewTreatmentMenu(!showNewTreatmentMenu)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Treatment
            <ChevronDown className="w-4 h-4" />
          </button>
          {showNewTreatmentMenu && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-[#e8e0f0] py-1 z-10">
              <button 
                onClick={() => openNewTreatmentModal("booking")}
                className="w-full px-4 py-2 text-left text-sm text-[#3d3d3d] hover:bg-[#f8f5fc] transition-colors"
              >
                From Booking
              </button>
              <button 
                onClick={() => openNewTreatmentModal("walkin")}
                className="w-full px-4 py-2 text-left text-sm text-[#3d3d3d] hover:bg-[#f8f5fc] transition-colors"
              >
                Walk-in Treatment
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveFilter(tab.id)}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              activeFilter === tab.id
                ? "bg-[#704aaf] text-white"
                : "bg-white text-[#5c5c5c] hover:bg-[#f0ebf5] border border-transparent"
            }`}
          >
            {activeFilter === tab.id && tab.count !== undefined && (
              <Check className="w-4 h-4" />
            )}
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                  activeFilter === tab.id
                    ? "bg-white/20 text-white"
                    : "bg-[#f0ebf5] text-[#704aaf]"
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}

        <div className="ml-auto">
          <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#6b6b6b] bg-white border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors">
            Advanced Filters
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Treatments Table */}
      <div className="bg-white rounded-xl overflow-hidden border border-[#e8e0f0]">
        <table className="w-full">
          <thead>
            <tr className="bg-[#f3eff8] border-b border-[#e8e0f0]">
              <th className="text-left px-5 py-3.5 text-[13px] font-semibold text-[#6b5b7a] tracking-wide">
                Client
              </th>
              <th className="text-left px-5 py-3.5 text-[13px] font-semibold text-[#6b5b7a] tracking-wide">
                Treatment
              </th>
              <th className="text-left px-5 py-3.5 text-[13px] font-semibold text-[#6b5b7a] tracking-wide">
                Practitioner
              </th>
              <th className="text-left px-5 py-3.5 text-[13px] font-semibold text-[#6b5b7a] tracking-wide">
                Consent
              </th>
              <th className="text-left px-5 py-3.5 text-[13px] font-semibold text-[#6b5b7a] tracking-wide">
                Status
              </th>
              <th className="text-left px-5 py-3.5 text-[13px] font-semibold text-[#6b5b7a] tracking-wide">
                Date
              </th>
              <th className="text-left px-5 py-3.5 text-[13px] font-semibold text-[#6b5b7a] tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredTreatments.map((treatment) => (
              <tr
                key={treatment.id}
                onClick={() =>
                  navigate(`/aesthetics/treatments/${treatment.id}`)
                }
                className="border-b border-[#f0ebf5] hover:bg-[#faf8fc] transition-colors cursor-pointer group"
              >
                {/* Client */}
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ${treatment.client.avatarBg}`}
                    >
                      {treatment.client.initials}
                    </div>
                    <span className="text-[14px] font-medium text-[#3d3d3d]">
                      {treatment.client.name}
                    </span>
                  </div>
                </td>

                {/* Treatment */}
                <td className="px-5 py-3.5 text-[14px] text-[#5c5c5c]">
                  {treatment.treatment}
                </td>

                {/* Practitioner */}
                <td className="px-5 py-3.5 text-[14px] text-[#5c5c5c]">
                  {treatment.practitioner}
                </td>

                {/* Consent */}
                <td className="px-5 py-3.5">
                  <ConsentBadge
                    status={treatment.consent}
                    signed={treatment.consentSigned}
                  />
                </td>

                {/* Status */}
                <td className="px-5 py-3.5">
                  <StatusBadge
                    status={treatment.status}
                    pending={treatment.statusPending}
                    signed={treatment.consentSigned}
                  />
                </td>

                {/* Date */}
                <td className="px-5 py-3.5 text-[14px] text-[#5c5c5c] whitespace-nowrap">
                  {treatment.date} - {treatment.time}
                </td>

                    {/* Actions */}
                    <td className="px-5 py-3.5">
                      <ActionButtons
                        status={treatment.status}
                        onView={(e) => {
                          e.stopPropagation();
                          navigate(`/aesthetics/treatments/${treatment.id}`);
                        }}
                        onReschedule={(e) => {
                          e.stopPropagation();
                          openRescheduleModal(treatment);
                        }}
                        onResume={(e) => {
                          e.stopPropagation();
                          navigate(`/aesthetics/treatments/${treatment.id}`);
                        }}
                        onComplete={(e) => {
                          e.stopPropagation();
                          openCompleteModal(treatment);
                        }}
                      />
                    </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {filteredTreatments.length === 0 && (
        <div className="bg-white rounded-xl border border-[#e8e0f0] p-12 text-center mt-6">
          <h3 className="text-lg font-semibold text-[#2d2d2d] mb-2">
            No treatments found
          </h3>
          <p className="text-[#6b6b6b] mb-4">
            There are no treatments matching your current filter.
          </p>
          <button
            onClick={() => setActiveFilter("all")}
            className="text-[#704aaf] font-medium hover:underline"
          >
            View all treatments
          </button>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showNewTreatmentMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowNewTreatmentMenu(false)}
        />
      )}

      {/* New Treatment Modal */}
      <NewTreatmentModal
        isOpen={showNewTreatmentModal}
        onClose={() => setShowNewTreatmentModal(false)}
        onSave={handleNewTreatmentSave}
        mode={treatmentModalMode}
      />

      {/* Reschedule Treatment Modal */}
      <RescheduleTreatmentModal
        isOpen={showRescheduleModal}
        onClose={() => {
          setShowRescheduleModal(false);
          setSelectedTreatmentForReschedule(null);
        }}
        onSave={handleRescheduleSave}
        treatment={
          selectedTreatmentForReschedule
            ? {
                id: selectedTreatmentForReschedule.id,
                clientName: selectedTreatmentForReschedule.client.name,
                treatmentName: selectedTreatmentForReschedule.treatment,
                currentDate: selectedTreatmentForReschedule.date,
                currentTime: selectedTreatmentForReschedule.time,
              }
            : undefined
        }
      />

      {/* Complete Treatment Modal */}
      <CompleteTreatmentModal
        isOpen={showCompleteModal}
        onClose={() => {
          setShowCompleteModal(false);
          setSelectedTreatmentForComplete(null);
        }}
        onComplete={handleCompleteTreatment}
        treatment={
          selectedTreatmentForComplete
            ? {
                id: selectedTreatmentForComplete.id,
                clientName: selectedTreatmentForComplete.client.name,
                treatmentName: selectedTreatmentForComplete.treatment,
                practitioner: selectedTreatmentForComplete.practitioner,
                price: 120, // In a real app, this would come from the treatment data
                paidAmount: 50, // In a real app, this would come from the treatment data
                consentSigned: selectedTreatmentForComplete.consentSigned || false,
              }
            : undefined
        }
      />
    </div>
  );
}
